import { forwardRef, useEffect, useImperativeHandle, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import TextField from "../Form/TextField";
import { useTranslationApp } from "../../lib/i18next";
import { NAME_PLACEHOLDER, FIELD_REQUIRED } from "../../i18n/keysTranslations";

const DEFAULT_EDIT_FIELD_SX = {
  width: 300,
  "& .MuiInputBase-root": {
    fontSize: 20,
  },
};

const pickTypographyStyle = (theme, variantText, sxText) => {
  const variantStyle = theme.typography[variantText] || theme.typography.body1;

  return {
    fontSize: sxText.fontSize ?? variantStyle.fontSize,
    fontWeight: sxText.fontWeight ?? variantStyle.fontWeight,
    lineHeight: sxText.lineHeight ?? variantStyle.lineHeight,
    letterSpacing: sxText.letterSpacing ?? variantStyle.letterSpacing,
    textAlign: sxText.textAlign ?? "inherit",
  };
};

const buildMatchedEditFieldSx = (typographyStyle, text, editFullWidth) => {
  const charCount = Math.max(String(text || "").length, 1);

  const inputWidthSx = editFullWidth
    ? {
        width: "100%",
        textAlign: typographyStyle.textAlign,
      }
    : {
        width: `${charCount}ch`,
        minWidth: `${Math.min(Math.max(charCount, 4), 24)}ch`,
        maxWidth: "100%",
        textAlign: typographyStyle.textAlign,
        fieldSizing: "content",
      };

  return {
    width: editFullWidth ? "100%" : "auto",
    minWidth: 0,
    maxWidth: "100%",
    display: editFullWidth ? "block" : "inline-flex",
    "& .MuiFormHelperText-root": {
      display: "none",
    },
    "& .MuiInputBase-root": {
      fontSize: typographyStyle.fontSize,
      fontWeight: typographyStyle.fontWeight,
      lineHeight: typographyStyle.lineHeight,
      letterSpacing: typographyStyle.letterSpacing,
      py: 0,
      minHeight: "unset",
    },
    "& .MuiInputBase-input": {
      padding: 0,
      height: "auto",
      ...inputWidthSx,
    },
  };
};

const EditableText = forwardRef(({
  value,
  sx,
  sxText = {},
  sxInput = {},
  onCallbackSave = () => { },
  onEditingChange,
  outsideClickRootRef,
  variantText = "body1",
  disabledEditable = false,
  matchTextStyle = false,
  editFullWidth = false,
  showTooltip = true,
}, ref) => {
  const theme = useTheme();
  const originalValueRef = useRef(value);
  const {
    control,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      text: value,
    },
  });
  const [isEditing, setIsEditing] = useState(false);
  const textFieldRef = useRef(null);
  const onSaveRef = useRef(() => {});

  const { t } = useTranslationApp();
  const text = watch("text");

  const enterEditMode = () => {
    if (disabledEditable) return;
    originalValueRef.current = value;
    setIsEditing(true);
  };

  useImperativeHandle(
    ref,
    () => ({
      startEditing: enterEditMode,
      save: () => onSaveRef.current(),
    }),
    // eslint-disable-next-line
    [disabledEditable, value]
  );

  useEffect(() => {
    if (isEditing) return;
    originalValueRef.current = value;
  }, [isEditing, value]);

  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  const onSave = () => {
    const fallbackName = originalValueRef.current;
    const nextName = String(text || "").trim();
    if (!nextName) {
      setValue("text", fallbackName);
      onCallbackSave(fallbackName);
    } else if (nextName !== String(value || "").trim()) {
      onCallbackSave(nextName);
    }
    setIsEditing(false);
  };
  onSaveRef.current = onSave;

  // Handle keyboard events for saving (Enter) and canceling (Escape)
  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      onSave();
    } else if (e.key === "Escape") {
      setValue("text", value);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    setValue("text", value);
    // eslint-disable-next-line
  }, [value]);

  useEffect(() => {
    const onClickOutside = (event) => {
      const root =
        outsideClickRootRef?.current || textFieldRef.current;
      if (root && !root.contains(event.target)) {
        onSaveRef.current();
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", onClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [isEditing, outsideClickRootRef]);

  const editFieldSx = matchTextStyle
    ? {
        ...buildMatchedEditFieldSx(
          pickTypographyStyle(theme, variantText, sxText),
          text,
          editFullWidth
        ),
        ...sxInput,
      }
    : {
        ...DEFAULT_EDIT_FIELD_SX,
        ...sxInput,
      };

  const nameTypography = (
    <Typography
      variant={variantText}
      sx={{
        ...sxText,
        cursor: disabledEditable ? "default" : "pointer",
      }}
      onClick={enterEditMode}
    >
      {text}
    </Typography>
  );

  return (
    <Box
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        ...sx,
        display: "flex",
        alignItems: "center",
      }}
    >
      {isEditing ? (
        // Container div for click outside detection
        <div ref={textFieldRef}>
          <TextField
            control={control}
            name={"text"}
            fullWidth={matchTextStyle ? editFullWidth : true}
            placeholder={t(NAME_PLACEHOLDER)}
            variant="standard"
            rules={{
              required: { value: true, message: t(FIELD_REQUIRED) },
            }}
            errors={errors}
            onKeyDown={onKeyDown}
            autoFocus
            sx={editFieldSx}
          />
        </div>
      ) : showTooltip ? (
        <Tooltip title={text}>{nameTypography}</Tooltip>
      ) : (
        nameTypography
      )}
    </Box>
  );
});

EditableText.displayName = "EditableText";

export default EditableText;
